import type { PickerProps } from "antd/es/date-picker/generatePicker";
import { startOfDay } from "date-fns";
import type { PickerRef } from "rc-picker";

import { getDateFromMinutesOfDay } from "@/modules/bookings/helpers";

import { CalendarIcon } from "../Icons/CalendarIcon";
import { disabledTime } from "./disabled-time";
import { AntDatePicker } from "./picker";

export type DatePickerValue = Date | "";

export interface DatePickerProps extends React.RefAttributes<PickerRef> {
  id?: string;
  name?: string;
  placeholder?: string;
  value: DatePickerValue;
  defaultValue?: Date;
  onChange: (value: DatePickerValue) => void;
  min?: Date;
  max?: Date;
  disabledDate?: (current: Date) => boolean;
  type?: "date" | "datetime" | "time";
  minuteStep?: PickerProps<Date>["minuteStep"];
  enableMidnight?: boolean;
  showNow?: boolean;
  allowClear?: boolean;
  error?: boolean;
  "data-testid"?: string;
  ref?: React.Ref<PickerRef>;
}

export function DatePicker({
  value,
  defaultValue,
  onChange,
  min,
  max,
  disabledDate,
  type = "date",
  minuteStep,
  enableMidnight = false,
  showNow = true,
  allowClear = false,
  error,
  ref,
  ...props
}: DatePickerProps): React.ReactNode {
  let format = "DD/MM/YYYY HH:mm";
  if (type === "time") {
    format = "HH:mm";
  } else if (type === "date") {
    format = "DD/MM/YYYY";
  }

  return (
    <AntDatePicker
      {...props}
      ref={ref}
      picker={type === "time" ? "time" : "date"}
      onChange={onChange}
      onCalendarChange={(val) => (val instanceof Date ? onChange(val) : onChange(""))}
      format={{
        format,
        type: "mask",
      }}
      showTime={
        type === "datetime" || type === "time" ? { format: "HH:mm", defaultOpenValue: defaultValue || min } : undefined
      }
      disabledTime={(current: Date) => disabledTime(enableMidnight, current, min, max)}
      minuteStep={minuteStep}
      value={typeof value === "number" ? getDateFromMinutesOfDay(value) : value}
      defaultValue={defaultValue}
      minDate={min}
      maxDate={max}
      disabledDate={(date) => {
        if (min && date < startOfDay(min)) {
          return true;
        }

        if (max && date >= max) {
          return true;
        }

        if (disabledDate) {
          return disabledDate(date);
        }

        return false;
      }}
      showNow={showNow}
      allowClear={allowClear}
      needConfirm={false}
      status={error ? "error" : undefined}
      size="large"
      suffixIcon={<CalendarIcon size={22} className="text-grey-700" />}
      className="w-full border-grey-300 [&_input]:placeholder:!text-grey-500 [&_svg]:text-grey-500"
    />
  );
}
